import React, { useState, useEffect, useRef } from 'react';
import { useBusiness } from '../../context/BusinessContext';
import { api } from '../../services/api';
import { Upload, AlertCircle, Phone, MessageCircle, Info, RefreshCw, Bug, Send, Copy } from 'lucide-react';
import { Card } from '../shared/Card';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const PlaygroundSection = () => {
  // --- Settings state ---
  const { currentBusiness, setCurrentBusiness } = useBusiness();
  const [settings, setSettings] = useState({
    basePrompt: '',
    welcomeMessage: '',
    primaryColor: '#2563EB',
    whatsappNumber: '',
    enableWhatsappButton: false,
    temperature: 0.1 // Add temperature to settings
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState({ type: '', content: '' });
  const [lastUpdated, setLastUpdated] = useState(null);

  // --- Test Chat state ---
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const messagesEndRef = useRef(null);

  // --- Settings logic ---
  useEffect(() => {
    if (currentBusiness?.business_id) {
      loadSettings();
      initializeChat();
    }
    // eslint-disable-next-line
  }, [currentBusiness?.business_id]);

  const loadSettings = async () => {
    try {
      setSettingsLoading(true);
      const response = await api.getBusiness(currentBusiness.business_id);
      if (response.data?.settings) {
        const loadedSettings = response.data.settings;
        setSettings({
          basePrompt: loadedSettings.basePrompt || '',
          welcomeMessage: loadedSettings.welcomeMessage || '',
          primaryColor: loadedSettings.primaryColor || '#2563EB',
          whatsappNumber: loadedSettings.whatsappNumber || '',
          enableWhatsappButton: loadedSettings.enableWhatsappButton || false,
          temperature: typeof loadedSettings.temperature === 'number' ? loadedSettings.temperature : 0.1 // Load temperature
        });
        setLastUpdated(response.data.updated_at);
      } else {
        setSettingsMessage({ type: 'error', content: 'No settings found for this business' });
      }
    } catch (error) {
      setSettingsMessage({ type: 'error', content: 'Failed to load settings' });
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSettingsLoading(true);
      if (!settings.basePrompt.trim()) {
        setSettingsMessage({ type: 'error', content: 'Base prompt cannot be empty' });
        return;
      }
      if (settings.enableWhatsappButton && !settings.whatsappNumber) {
        setSettingsMessage({
          type: 'error',
          content: 'WhatsApp number is required to enable WhatsApp button'
        });
        return;
      }
      if (settings.whatsappNumber && !/^\+?\d{10,15}$/.test(settings.whatsappNumber)) {
        setSettingsMessage({
          type: 'error',
          content: 'Please enter a valid WhatsApp number'
        });
        return;
      }
      const updatedSettings = {
        settings: {
          ...currentBusiness.settings,
          basePrompt: settings.basePrompt.trim(),
          welcomeMessage: settings.welcomeMessage.trim(),
          whatsappNumber: settings.whatsappNumber.trim(),
          enableWhatsappButton: settings.enableWhatsappButton,
          temperature: settings.temperature // Save temperature
        }
      };
      await api.updateBusiness(currentBusiness.business_id, updatedSettings);
      setCurrentBusiness({
        ...currentBusiness,
        settings: updatedSettings.settings
      });
      setSettingsMessage({ type: 'success', content: 'Settings saved successfully' });
      loadSettings();
    } catch (error) {
      setSettingsMessage({ type: 'error', content: 'Failed to save settings' });
    } finally {
      setSettingsLoading(false);
      setTimeout(() => setSettingsMessage({ type: '', content: '' }), 3000);
    }
  };

  // --- Test Chat logic ---
  const generateConversationId = () => Math.random().toString(36).substring(2, 15);

  const createMessageObject = (content, role) => {
    const now = new Date();
    return {
      id: now.getTime(),
      role: role,
      content: content,
      time: now.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }),
      timestamp: now.toISOString()
    };
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const initializeChat = async () => {
    if (!currentBusiness?.business_id || isInitializing) return;
    setIsInitializing(true);
    setConversationId(generateConversationId());
    setMessages([
      createMessageObject(
        currentBusiness?.settings?.welcomeMessage || 'Hello! How can I assist you today?',
        'assistant'
      )
    ]);
    setIsInitializing(false);
  };

  const resetChat = async () => {
    if (isInitializing || loading) return;
    initializeChat();
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !currentBusiness?.business_id || loading) return;
    setLoading(true);
    setIsTyping(true);
    const messageText = newMessage;
    setNewMessage('');
    const userMessage = createMessageObject(messageText, 'user');
    setMessages(prev => [...prev, userMessage]);
    try {
      const startTime = performance.now();
      const response = await api.testChat(currentBusiness.business_id, {
        message: messageText,
        chat_history: [...messages, userMessage],
        whatsapp_enabled: settings.enableWhatsappButton,
        whatsapp_number: settings.whatsappNumber,
        temperature: settings.temperature // Pass temperature to backend
      });
      const assistantMessage = createMessageObject(response.data.response, 'assistant');
      if (typeof response.data.confidence_score === 'number') {
        assistantMessage.confidence_score = response.data.confidence_score;
      }
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error(`Error: ${error.message || 'Failed to send message'}`);
    } finally {
      setLoading(false);
      setIsTyping(false);
    }
  };

  const sanitizeUrl = (url) => {
    if (!url) return '';
    return url.trim().replace(/\s+/g, '');
  };

  const isImageUrl = (url) => {
    if (!url) return false;
    const cleanUrl = sanitizeUrl(url).toLowerCase();
    return /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/.test(cleanUrl);
  };

  const preprocessContent = (content) => {
    if (!content) return '';
    content = content.replace(
      /!\[([^\]]*)\]\(([^)]+)\)|(?<!!)\[([^\]]+)\]\(([^)]+)\)/g,
      (match, imgAlt, imgUrl, linkText, linkUrl) => {
        if (imgAlt !== undefined) {
          return `![${imgAlt}](${sanitizeUrl(imgUrl)})`;
        } else {
          return `[${linkText}](${sanitizeUrl(linkUrl)})`;
        }
      }
    );
    content = content.replace(
      /\[WhatsApp Link\]\((https:\/\/wa\.me\/[^)]+)\)/gi,
      (match, whatsappUrl) => {
        return `[<span class="inline-flex items-center"><svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"></path><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm6.293 17.293c-1.684 1.684-3.924 2.612-6.293 2.612s-4.61-.928-6.293-2.612C3.895 15.609 2.965 13.37 2.965 11s.93-4.61 2.614-6.293C7.26 2.886 9.5 1.965 11.87 1.965s4.61.922 6.292 2.607c1.684 1.684 2.613 3.924 2.613 6.293s-.93 4.61-2.613 6.293z"></path></svg> Connect on WhatsApp</span>](${whatsappUrl})`;
      }
    );
    return content;
  };

  const formatDate = (timestamp, includeDate = false) => {
    if (!timestamp) return '';
    try {
      let date;
      if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'number') {
        date = timestamp.toString().length > 10 
          ? new Date(timestamp) 
          : new Date(timestamp * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        return 'Invalid date';
      }
      if (isNaN(date.getTime())) return 'Invalid date';
      if (includeDate) {
        return date.toLocaleDateString([], { 
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit', 
          minute: '2-digit'
        });
      } else {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
    } catch {
      return 'Date error';
    }
  };

  const ChatMessage = ({ message }) => {
    const isUser = message.role === 'user';
    const timestamp = message.timestamp || message.time || new Date().toISOString();
    const formattedTime = formatDate(timestamp, false);

    // Confidence UI color logic
    let confidenceColor = '';
    if (typeof message.confidence_score === 'number') {
      if (message.confidence_score >= 0.8) {
        confidenceColor = 'bg-green-500 text-white';
      } else if (message.confidence_score >= 0.5) {
        confidenceColor = 'bg-yellow-400 text-black';
      } else {
        confidenceColor = 'bg-red-500 text-white';
      }
    }

    // Dynamically set bubble width based on message length
    const contentLength = (message.content || '').length;
    let bubbleWidthClass = 'max-w-[85%] md:max-w-3xl';
    if (contentLength < 40) {
      bubbleWidthClass = 'max-w-xs md:max-w-xs';
    } else if (contentLength < 120) {
      bubbleWidthClass = 'max-w-md md:max-w-md';
    } else if (contentLength < 300) {
      bubbleWidthClass = 'max-w-lg md:max-w-lg';
    }

    return (
      <div className="flex flex-col items-end md:items-end">
        <div className={`${bubbleWidthClass} ${isUser ? 'ml-auto' : 'mr-auto'} transition-all duration-300 ease-in-out`}>
          <div className={`p-4 rounded-xl text-sm md:text-base shadow-sm ${
            isUser 
              ? 'bg-gradient-to-r from-[#1d2840] to-[#3DA3DC] text-white' 
              : 'bg-gray-50 border border-gray-200'
          } relative`}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                img: ({ node, ...props }) => {
                  if (!isImageUrl(props.src)) return null;
                  return (
                    <div className="my-3">
                      <img
                        {...props}
                        src={sanitizeUrl(props.src)}
                        alt={props.alt || 'Chat image'}
                        className="max-w-full h-auto rounded-lg shadow-sm"
                        loading="lazy"
                      />
                    </div>
                  );
                },
                a: ({ node, ...props }) => {
                  if (isImageUrl(props.href)) return null;
                  const href = props.href || '';
                  const isPhoneLink = href.startsWith('tel:');
                  const isWhatsAppLink = href.startsWith('https://wa.me/');
                  if (isWhatsAppLink) {
                    return (
                      <a
                        {...props}
                        href={sanitizeUrl(href)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${
                          isUser ? 'bg-white text-green-600' : 'bg-green-600 text-white'
                        } px-3 py-1.5 rounded-full inline-flex items-center no-underline hover:opacity-90 transition-opacity font-medium text-sm`}
                      >
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"></path>
                          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm6.293 17.293c-1.684 1.684-3.924 2.612-6.293 2.612s-4.61-.928-6.293-2.612C3.895 15.609 2.965 13.37 2.965 11s.93-4.61 2.614-6.293C7.26 2.886 9.5 1.965 11.87 1.965s4.61.922 6.292 2.607c1.684 1.684 2.613 3.924 2.613 6.293s-.93 4.61-2.613 6.293z"></path>
                        </svg>
                        Connect on WhatsApp
                      </a>
                    );
                  }
                  return (
                    <a
                      {...props}
                      href={isPhoneLink ? href : sanitizeUrl(href)}
                      target={isPhoneLink ? '_self' : '_blank'}
                      rel={isPhoneLink ? '' : 'noopener noreferrer'}
                      className={`${
                        isUser ? 'text-white' : 'text-blue-600'
                      } underline hover:opacity-80 inline-flex items-center font-medium`}
                    >
                      {props.children}
                    </a>
                  );
                },
                p: ({ children }) => (
                  <p className="mb-1 whitespace-pre-wrap">{children}</p>
                ),
                strong: ({ children }) => (
                  <strong className={`font-semibold ${
                    isUser ? 'text-white' : 'text-gray-900'
                  }`}>
                    {children}
                  </strong>
                ),
              }}
            >
              {preprocessContent(message.content)}
            </ReactMarkdown>
          </div>
          {/* Timestamp and confidence score outside bubble, right for user, left for assistant */}
          <div className={`text-xs mt-1 opacity-60 flex items-center gap-2 ${isUser ? 'justify-end text-right' : 'justify-start text-left'}`}>
            {formattedTime}
            {!isUser && typeof message.confidence_score === 'number' && (
              <span
                className={`px-2 py-0.5 rounded-full font-semibold cursor-default ${confidenceColor}`}
                title={`Confidence Score: ${message.confidence_score.toFixed(2)}`}
                style={{ minWidth: 32, textAlign: 'center' }}
              >
                {message.confidence_score.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const TypingIndicator = () => (
    <div className="max-w-xs mr-auto">
      <div className="p-3 rounded-lg bg-gray-100">
        <div className="flex gap-2">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
        </div>
      </div>
    </div>
  );
const textareaRef = useRef(null);

useEffect(() => {
  const textarea = textareaRef.current;
  if (textarea) {
    textarea.style.height = "auto"; // Reset height
    textarea.style.height = textarea.scrollHeight + "px"; // Set to content height
  }
}, [settings.basePrompt]);

  // --- Render ---
  // Add document status state
  const [docStatus, setDocStatus] = useState(null);

  useEffect(() => {
    const fetchDocStatus = async () => {
      if (!currentBusiness?.business_id) return;
      try {
        const response = await api.listDocuments(currentBusiness.business_id);
        const docs = response.data?.documents || [];
        const hasIndexed = docs.some(doc => doc.status === 'indexed');
        setDocStatus(hasIndexed ? 'trained' : 'untrained');
      } catch {
        setDocStatus(null);
      }
    };
    fetchDocStatus();
  }, [currentBusiness?.business_id]);

  // --- Render ---
  return (
    <div className="p-2 sm:p-6 mt-6 sm:mt-10 md:mt-0">
      {settingsMessage.content && (
        <div className={`mb-4 p-4 rounded-md flex items-center ${
          settingsMessage.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        }`}>
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>{settingsMessage.content}</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 h-full items-start">
        {/* Left: Settings */}
        <div className="flex flex-col gap-4 sm:gap-6 self-start w-full max-w-full md:max-w-md h-auto md:h-[665px] overflow-y-auto">
          <div className="flex justify-start">
            <button
              onClick={handleSaveSettings}
              disabled={settingsLoading}
              className="px-4 sm:px-6 py-2 bg-gradient-to-r from-[#1d2840] to-[#3DA3DC] text-white rounded-full
                       disabled:opacity-50 hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
            >
              {settingsLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
          {/* Status indicator above Instructions - right aligned */}
          <div className="mb-2 flex items-center justify-between">
            <span className="font-semibold text-[#1d2840]">Status:</span>
            <div>
              {docStatus === 'trained' ? (
                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">
                  <span className="w-2 h-2 rounded-full bg-green-500 inline-block mr-1" />
                  Trained
                </span>
              ) : docStatus === 'untrained' ? (
                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-sm font-semibold">
                  <span className="w-2 h-2 rounded-full bg-red-500 inline-block mr-1" />
                  Untrained
                </span>
              ) : null}
            </div>
          </div>
          <Card className="w-full">
            <h2 className="text-lg font-medium text-[#1d2840] mb-4">Instructions</h2>
            <textarea
              ref={textareaRef}
              value={settings.basePrompt}
              onChange={(e) => {
                setSettings({ ...settings, basePrompt: e.target.value });
              }}
              placeholder="Enter your base prompt configuration..."
              className="w-full p-3 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:ring-1 focus:ring-[#3DA3DC] focus:outline-none resize-none overflow-hidden"
              rows={1}
              style={{ minHeight: "120px" }}
            />
            {/* Temperature slider/input */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model Temperature
              </label>
              <span className="block text-xs text-gray-500 mb-2">
                (Controls randomness, 0 = deterministic, 1 = creative)
              </span>
              <div className="flex items-center gap-3 w-full">
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={settings.temperature}
                  onChange={e => setSettings({ ...settings, temperature: parseFloat(e.target.value) })}
                  className="w-full"
                />
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={settings.temperature}
                  onChange={e => setSettings({ ...settings, temperature: Math.max(0, Math.min(1, parseFloat(e.target.value) || 0)) })}
                  className="w-16 px-2 py-1 border rounded text-sm"
                />
              </div>
            </div>
          </Card>
          <Card className="w-full">
            <h2 className="text-lg font-medium text-[#1d2840] mb-4">Welcome Message</h2>
            <textarea
              value={settings.welcomeMessage}
              onChange={(e) => setSettings({ ...settings, welcomeMessage: e.target.value })}
              placeholder="Enter welcome message..."
              className="w-full h-24 sm:h-32 p-3 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:ring-1 focus:ring-[#3DA3DC] focus:outline-none"
            />
          </Card>
          <Card className="w-full">
            <h2 className="text-lg font-medium text-[#1d2840] mb-4">WhatsApp Integration</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="whatsappNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    id="whatsappNumber"
                    value={settings.whatsappNumber}
                    onChange={(e) => setSettings({ ...settings, whatsappNumber: e.target.value })}
                    placeholder="+919611896916"
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:border-[#3DA3DC] focus:ring-1 focus:ring-[#3DA3DC] focus:outline-none"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter your WhatsApp number with country code (e.g., +919611896916)
                </p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableWhatsapp"
                  checked={settings.enableWhatsappButton}
                  onChange={(e) => setSettings({ ...settings, enableWhatsappButton: e.target.checked })}
                  className="h-4 w-4 text-[#3DA3DC] focus:ring-[#3DA3DC] border-gray-300 rounded"
                  disabled={!settings.whatsappNumber}
                />
                <label htmlFor="enableWhatsapp" className="ml-2 block text-sm text-gray-700">
                  Enable WhatsApp Button
                </label>
              </div>
              <p className="text-xs text-gray-500">
                When enabled, users will have the option to connect via WhatsApp for queries that need human intervention.
              </p>
            </div>
          </Card>
        </div>
        {/* Right: Test Chat */}
        <div className="flex flex-col items-center justify-center w-full h-auto md:h-[665px] relative">
          <div className="flex justify-center w-full">
            <Card className="flex flex-col h-[475px] sm:h-[570px] md:h-[665px] w-full max-w-full sm:max-w-lg md:w-[750px] overflow-hidden sticky top-0 z-10 md:-ml-48">
              <div className="flex items-center justify-between w-full mb-4">
                <div className="flex items-center">
                  <MessageCircle className="w-5 h-5 text-[#3DA3DC] mr-2" />
                  <h2 className="text-lg font-medium text-[#1d2840]">Chat Preview</h2>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={resetChat}
                    disabled={isInitializing || loading}
                    className="px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md flex items-center disabled:opacity-50 justify-center"
                    aria-label="Reset chat conversation"
                  >
                    <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Reset Chat</span>
                  </button>
                </div>
              </div>
              <div className="flex-1 border border-gray-200 rounded-lg flex flex-col overflow-hidden">
                <div className="flex-1 p-2 sm:p-4 overflow-y-auto space-y-4 relative" id="chat-messages-container">
                  {messages.map(message => (
                    <ChatMessage key={message.id} message={message} />
                  ))}
                  {isTyping && <TypingIndicator />}
                  <div ref={messagesEndRef} className="h-1" />
                </div>
                <div className="p-2 sm:p-3 border-t border-gray-200 sticky bottom-0 bg-white">
                  <div className="relative">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && !loading && handleSendMessage()}
                      placeholder="Type your message..."
                      className="w-full pl-4 pr-12 py-2 rounded-full border border-gray-300 focus:border-[#3DA3DC] focus:ring-1 focus:ring-[#3DA3DC] focus:outline-none text-sm"
                      disabled={loading || isInitializing}
                      aria-label="Chat message input"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={loading || !newMessage.trim() || isInitializing}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-gradient-to-r from-[#1d2840] to-[#3DA3DC] text-white rounded-full 
                            disabled:opacity-50 hover:shadow-lg transform hover:-translate-y-[52%] transition-all"
                      aria-label="Send message"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaygroundSection;


